import { v4 as uuidv4 } from "uuid"

import { formatDate, formatDateAgo, parseDate } from "@/utils/dateUtil"
import { syncDescriptionHtml } from "@/utils/syncDescriptionHtml"

import { CommentSourceType, ResourceType } from "./common"
import { StructuredDescription } from "./EditorData"
import { EmojiType } from "./Emoji"
import { FileMetaData, IFile } from "./File"
import { IdeaSize, IdeaStatus } from "./Idea"
import { ReferencedResourcePreviews } from "./Search"
import { Task } from "./Task"
import { User } from "./User"

export const commentChangeType = ["CONTENT_CHANGE", "DELETED"]

export type CommentChangeType = (typeof commentChangeType)[number]

export interface ReturnCommentObj {
  structuredDescription: StructuredDescription
  targetResourceId?: CommentTarget
  source?: Source
  replyOnCommentId?: string
  attachmentIds?: string[]
  deletedFileMetadataIds?: string[]
  feContext?: CommentFeContext
  demoVideoId?: string
  demoVideoTimestamp?: number | null
}

export interface PayloadCommentCount {
  projectId?: string
  /** Set false when the target belongs to the account rather than the currently opened project. */
  projectScoped?: boolean
  targetId: string
  onlyGet?: boolean
}

export interface CommentTarget {
  id: string
  type?: ResourceType
}

export type CommentProps = Partial<Comment>

export enum CommentContentType {
  REGULAR = "REGULAR",
  DELETE_TASK = "DELETE_TASK",
  RESTORE_TASK = "RESTORE_TASK",
  UNIMPLEMENTED = "UNIMPLEMENTED",
  TITLE = "TITLE",
  STRUCTURED_DESCRIPTION = "STRUCTURED_DESCRIPTION",
  ACCEPTANCE_CRITERIA = "ACCEPTANCE_CRITERIA",
  SPEC = "SPEC",
  OWNER = "OWNER",
  STATUS = "STATUS",
  ITEM_TYPE = "ITEM_TYPE",
  EPIC = "EPIC",
  SEVERITY_LEVEL = "SEVERITY_LEVEL",
  STORY_POINTS = "STORY_POINTS",
  TEAM = "TEAM",
  SPRINT = "SPRINT",
  TASKS = "TASKS",
  DEFINITIONS_OF_DONE = "DEFINITIONS_OF_DONE",
  BACKLOG_PRIORITY = "BACKLOG_PRIORITY",
  MOVE_TO_TASK = "MOVE_TO_TASK",
  FILE_METADATA_IS_DELETED = "FILE_METADATA_IS_DELETED",
  AUDIT_COMMENT_CHANGED = "AUDIT_COMMENT_CHANGED",
  CONTENT_CHANGE = "CONTENT_CHANGE",
  DELETED = "DELETED",
  ITEM_CONVERTED_TO_EPIC = "ITEM_CONVERTED_TO_EPIC",
  DEMO_VIDEO = "DEMO_VIDEO",
  IDEA_READY = "IDEA_READY",
  IDEA_STATUS = "IDEA_STATUS",
  IDEA_TITLE = "IDEA_TITLE",
  IDEA_SIZE = "IDEA_SIZE",
  IDEA_CONVERTED_TO_EPIC = "IDEA_CONVERTED_TO_EPIC",
  IDEA_CONVERTED_TO_FEATURE = "IDEA_CONVERTED_TO_FEATURE",
  IDEA_CONVERTED_TO_ITEM = "IDEA_CONVERTED_TO_ITEM",
  IDEA_CONVERTED_RESOURCE = "IDEA_CONVERTED_RESOURCE"
}

export const commentTypeByAuditRecord: Record<string, CommentContentType> = {
  regular: CommentContentType.REGULAR,

  unimplemented: CommentContentType.UNIMPLEMENTED,
  title: CommentContentType.TITLE,
  structuredDescription: CommentContentType.STRUCTURED_DESCRIPTION,
  acceptanceCriteria: CommentContentType.ACCEPTANCE_CRITERIA,
  spec: CommentContentType.SPEC,
  owner: CommentContentType.OWNER,
  status: CommentContentType.STATUS,
  itemType: CommentContentType.ITEM_TYPE,
  epic: CommentContentType.EPIC,
  severityLevel: CommentContentType.SEVERITY_LEVEL,
  storyPoints: CommentContentType.STORY_POINTS,
  team: CommentContentType.TEAM,
  sprint: CommentContentType.SPRINT,
  tasks: CommentContentType.TASKS,
  definitionsOfDone: CommentContentType.DEFINITIONS_OF_DONE,
  backlogPriority: CommentContentType.BACKLOG_PRIORITY,
  moveToTask: CommentContentType.MOVE_TO_TASK,
  deleteTask: CommentContentType.DELETE_TASK,
  restoreTask: CommentContentType.RESTORE_TASK,
  fileMetadataIsDeleted: CommentContentType.FILE_METADATA_IS_DELETED,
  demoFile: CommentContentType.DEMO_VIDEO
}

export interface EpicCommentTypeByAuditRecord {
  name: CommentContentType
  structuredDescription: CommentContentType
}

export type EpicAuditChange = keyof EpicCommentTypeByAuditRecord

export const epicCommentTypeByAuditRecord: EpicCommentTypeByAuditRecord = {
  name: CommentContentType.TITLE,
  structuredDescription: CommentContentType.STRUCTURED_DESCRIPTION
}

export interface IdeaCommentTypeByAuditRecord {
  ready: CommentContentType
  status: CommentContentType
  title: CommentContentType
  structuredDescription: CommentContentType
  size: CommentContentType
  CONVERTED_TO_EPIC: CommentContentType
  CONVERTED_TO_FEATURE: CommentContentType
  CONVERTED_TO_ITEM: CommentContentType
  FEATURE_DELETED: CommentContentType
  EPIC_DELETED: CommentContentType
  EPIC_ARCHIVED: CommentContentType
  ITEM_DELETE: CommentContentType
  ITEM_OBSOLETE: CommentContentType
  EPIC_REACTIVATED: CommentContentType
  ITEM_REACTIVATED: CommentContentType
}

export type IdeaAuditChange = keyof IdeaCommentTypeByAuditRecord

export const ideaCommentTypeByAuditRecord: IdeaCommentTypeByAuditRecord = {
  ready: CommentContentType.IDEA_READY,
  status: CommentContentType.IDEA_STATUS,
  title: CommentContentType.IDEA_TITLE,
  structuredDescription: CommentContentType.STRUCTURED_DESCRIPTION,
  size: CommentContentType.IDEA_SIZE,
  CONVERTED_TO_EPIC: CommentContentType.IDEA_CONVERTED_TO_EPIC,
  CONVERTED_TO_FEATURE: CommentContentType.IDEA_CONVERTED_TO_FEATURE,
  CONVERTED_TO_ITEM: CommentContentType.IDEA_CONVERTED_TO_ITEM,
  FEATURE_DELETED: CommentContentType.IDEA_CONVERTED_RESOURCE,
  EPIC_DELETED: CommentContentType.IDEA_CONVERTED_RESOURCE,
  EPIC_ARCHIVED: CommentContentType.IDEA_CONVERTED_RESOURCE,
  ITEM_DELETE: CommentContentType.IDEA_CONVERTED_RESOURCE,
  ITEM_OBSOLETE: CommentContentType.IDEA_CONVERTED_RESOURCE,
  EPIC_REACTIVATED: CommentContentType.IDEA_STATUS,
  ITEM_REACTIVATED: CommentContentType.IDEA_STATUS
}

export interface Source {
  sourceId: string
  sourceType: CommentSourceType
}

export class CommentByItem {
  comment: Comment
  itemId: string

  constructor(props: CommentByItem) {
    this.comment = Comment.create(props.comment)
    this.itemId = props.itemId
  }
}

export interface ItemAuditDemoFile {
  demoVideoUrl?: string
  demoFileMetadata?: FileMetaData | null
}

export interface CommentItemAuditItem {
  title: string | null
  structuredDescription: { html: string } | null
  acceptanceCriteria: { html: string } | null
  spec: { html: string } | null
  demoFile: ItemAuditDemoFile | null
  owner: User | null
  status: string | null
  itemType: string | null
  epicName: string | null
  epicSequenceNumber: number | null
  severityLevel: string | null
  storyPoints: number | null
  teamName: string | null
  teamPictureUrl: { pictureUrl: string } | null
  sprintSequenceNumber: number | null
  sprintStatus: string | null
  continuousSprint: boolean | null
  backlogPriority: number | null
  definitionOfDoneName: string | null
  definitionOfDoneState: boolean | null
  sequenceNumber: number | null
  deletedFileMetadata: FileMetaData | null
  ready: boolean | null
}

export interface CommentItemAuditRecord {
  after: CommentItemAuditItem
  before: CommentItemAuditItem
  fieldName: string
  taskBefore: Task | null
  taskAfter: Task | null
  restoreTaskUrl: string
}

export class CommentAuditRecord {
  after: Comment
  before: Comment
  changeType: CommentChangeType

  constructor(props: CommentAuditRecord) {
    this.after = Comment.create(props.after)
    this.before = Comment.create(props.before)
    this.changeType = props.changeType
  }
}

export interface EpicAuditRecord {
  change?: string
  before: EpicAuditRecordData
  after: EpicAuditRecordData
}

export interface EpicAuditRecordData {
  itemId?: string
  itemSequenceNumber?: number
  epicId?: string
  epicSequenceNumber?: number
  name?: string
  structuredDescription?: { html: string }
}

export interface IdeaAuditRecord {
  change?: string
  before: IdeaAuditRecordData
  after: IdeaAuditRecordData
}

export interface IdeaAuditRecordData {
  ideaId?: string
  ideaSequenceNumber?: number
  ready?: boolean
  status?: IdeaStatus
  title?: string
  structuredDescription?: { html: string }
  size?: IdeaSize
  epicId?: string
  epicSequenceNumber?: number
  epicTitle?: string
  featureId?: string
  featureTitle?: string
  itemId?: string
  itemSequenceNumber?: number
  itemTitle?: string
  itemLabel?: string
}

export class RepliedOnComment {
  deleted = false
  fileMetadata: FileMetaData[] = []
  id = ""
  owner: User = new User()
  structuredDescription: StructuredDescription = new StructuredDescription()

  constructor(props?: Partial<RepliedOnComment>) {
    if (!props) return
    Object.keys(props).forEach(key => {
      this[key] = props[key]
    })
    this.owner = new User(this.owner)
    this.structuredDescription = new StructuredDescription(this.structuredDescription)
    this.fileMetadata = this.fileMetadata.map(file => FileMetaData.create(file))
  }
}

export interface CommentFeContext {
  changeId: string
}

export class Comment {
  targetResourceId?: CommentTarget
  source?: Source
  id: string
  owner?: User
  assignees: User[]
  createdDate: Date
  fileMetadata?: FileMetaData[]
  structuredDescription: StructuredDescription
  referencedResourcePreviews?: ReferencedResourcePreviews
  itemAuditRecord?: CommentItemAuditRecord
  epicAuditRecord?: EpicAuditRecord
  ideaAuditRecord?: IdeaAuditRecord
  restoreTaskUrl: string
  reactionsWithUserPreviews: { [key: EmojiType]: User[] }
  replyOnCommentId?: string
  repliedOnComment?: RepliedOnComment
  deleted: boolean
  commentAuditRecord?: CommentAuditRecord
  attachmentIds?: string[]
  deletedFileMetadataIds?: string[]
  feContext: CommentFeContext
  isPending?: boolean
  demoVideoId: string
  demoVideoTimestamp: number | null
  constructor(props?: CommentProps) {
    this.setDefaultData()
    if (!props) return

    this.id = props.id ?? ""
    this.restoreTaskUrl = props.restoreTaskUrl ?? ""
    this.structuredDescription = props.structuredDescription ?? new StructuredDescription()
    this.targetResourceId = props.targetResourceId ?? undefined
    this.source = props.source
    this.owner = props.owner ? User.create(props.owner) : undefined
    this.assignees = props.assignees?.map(user => User.create(user)) ?? []
    this.createdDate = props.createdDate
      ? props.createdDate instanceof Date
        ? new Date(props.createdDate)
        : parseDate(String(props.createdDate), true)
      : new Date()
    this.fileMetadata = props.fileMetadata?.length
      ? props.fileMetadata
          .map((file: IFile) => FileMetaData.create(file))
          .sort((a, b) => {
            const nameA = a.name.toLowerCase()
            const nameB = b.name.toLowerCase()
            if (nameA < nameB) {
              return -1
            }
            if (nameA > nameB) {
              return 1
            }
            return 0
          })
      : []

    this.itemAuditRecord = props.itemAuditRecord ?? undefined
    this.epicAuditRecord = props.epicAuditRecord ?? undefined
    this.ideaAuditRecord = props.ideaAuditRecord ?? undefined
    if (props.referencedResourcePreviews) {
      this.structuredDescription.html = syncDescriptionHtml({
        referencedResourcePreviews: props.referencedResourcePreviews,
        structuredDescription: this.structuredDescription
      })
    }
    this.reactionsWithUserPreviews = props.reactionsWithUserPreviews ?? {}
    this.replyOnCommentId = props.replyOnCommentId ?? ""
    this.repliedOnComment = props.repliedOnComment
      ? new RepliedOnComment(props.repliedOnComment)
      : undefined
    this.deleted = props.deleted ?? false
    this.commentAuditRecord = props.commentAuditRecord
      ? new CommentAuditRecord(props.commentAuditRecord)
      : undefined
    this.attachmentIds = props.attachmentIds ?? []
    this.deletedFileMetadataIds = props.deletedFileMetadataIds ?? []
    this.feContext = props.feContext ?? { changeId: uuidv4() }
    this.demoVideoId = props.demoVideoId ?? ""
    this.demoVideoTimestamp = props.demoVideoTimestamp ?? null
    if (this.commentAuditRecord) {
      const after = this.commentAuditRecord.after
      this.structuredDescription = after.structuredDescription
      this.reactionsWithUserPreviews = after.reactionsWithUserPreviews
      this.fileMetadata = after.fileMetadata
    }
    this.isPending = props.isPending
  }

  setDefaultData() {
    this.targetResourceId = undefined
    this.source = undefined
    this.id = ""
    this.owner = undefined
    this.assignees = []
    this.createdDate = new Date()
    this.fileMetadata = []
    this.structuredDescription = new StructuredDescription()
    this.referencedResourcePreviews = undefined
    this.itemAuditRecord = undefined
    this.reactionsWithUserPreviews = {}
    this.replyOnCommentId = ""
    this.deleted = false
    this.attachmentIds = []
    this.deletedFileMetadataIds = []
    this.demoVideoId = ""
    this.demoVideoTimestamp = null
  }

  get type(): CommentContentType {
    const changeType = this.commentAuditRecord?.changeType
    if (changeType && commentChangeType.includes(changeType)) {
      return CommentContentType[changeType]
    }
    if (this.epicAuditRecord) {
      const change = this.epicAuditRecord.change
      if (change && change in epicCommentTypeByAuditRecord) {
        return epicCommentTypeByAuditRecord[change as EpicAuditChange]
      }
      if (this.isItemConvertedToEpicAuditRecord) {
        return CommentContentType.ITEM_CONVERTED_TO_EPIC
      }
      return CommentContentType.UNIMPLEMENTED
    }
    if (this.ideaAuditRecord) {
      const change = this.ideaAuditRecord.change
      if (change && change in ideaCommentTypeByAuditRecord) {
        return ideaCommentTypeByAuditRecord[change as IdeaAuditChange]
      }
      return CommentContentType.UNIMPLEMENTED
    }
    return (
      commentTypeByAuditRecord[this.itemAuditRecord?.fieldName ?? "regular"] ??
      commentTypeByAuditRecord["unimplemented"]
    )
  }

  get isItemConvertedToEpicAuditRecord(): boolean {
    const before = this.epicAuditRecord?.before
    const after = this.epicAuditRecord?.after

    return Boolean(
      (before?.itemId || before?.itemSequenceNumber != null) &&
        (after?.epicId || after?.epicSequenceNumber != null)
    )
  }

  get targetId() {
    return this.targetResourceId?.id ?? ""
  }

  get targetType() {
    return this.targetResourceId?.type ?? ""
  }

  getJsonObj(): ReturnCommentObj {
    return {
      structuredDescription: this.structuredDescription,
      targetResourceId: this.targetResourceId,
      source: this.source,
      replyOnCommentId: this.replyOnCommentId,
      attachmentIds: this.attachmentIds,
      deletedFileMetadataIds: this.deletedFileMetadataIds,
      feContext: this.feContext,
      demoVideoId: this.demoVideoId || undefined,
      demoVideoTimestamp: this.demoVideoId ? this.demoVideoTimestamp : undefined
    }
  }

  get createdDateDate() {
    return parseDate(String(this.createdDate), true)
  }

  get timeAgo() {
    return formatDateAgo({
      date: new Date(),
      dateFormat: this.createdDate,
      sufixAgo: true
    })
  }

  get timeAndDate() {
    return formatDate(this.createdDate, "MM/dd/yy h:mm bb")
  }

  get timeAndDateReversed() {
    return formatDate(this.createdDate, "MM/dd/yy hp")
  }

  get dayAndMonth() {
    const format =
      formatDate(this.createdDate, "yyyy") !== formatDate(new Date(), "yyyy")
        ? "MMMM dd, yyyy"
        : "MMMM dd"
    return formatDate(this.createdDate, format)
  }

  get isRegularComment(): boolean {
    return this.type === CommentContentType.REGULAR
  }

  get unimplemented(): boolean {
    return this.type === CommentContentType.UNIMPLEMENTED
  }

  get isUnsupportedIdeaAudit(): boolean {
    return !!this.ideaAuditRecord && this.unimplemented
  }

  private getDefinitionOfDoneStatusText(): string {
    if (!this.before?.definitionOfDoneName && this.after?.definitionOfDoneName) {
      return "added Definition of Done"
    } else if (this.before?.definitionOfDoneName && !this.after?.definitionOfDoneName) {
      return "deleted Definition of Done"
    }
    return "updated Definition of Done"
  }

  private getIdeaConvertedResourceText(): string {
    switch (this.ideaAuditRecord?.change) {
      case "FEATURE_DELETED":
        return "deleted the converted Roadmap Deliverable"
      case "EPIC_DELETED":
        return "deleted the converted Epic"
      case "EPIC_ARCHIVED":
        return "archived the converted Epic"
      case "ITEM_DELETE":
        return "deleted the converted Item"
      case "ITEM_OBSOLETE":
        return "marked the Converted Item as Obsolete"
      default:
        return "audit comment"
    }
  }

  get subtitleLabel(): string {
    switch (this.type) {
      case CommentContentType.TITLE:
        return "changed Title"
      case CommentContentType.OWNER:
        return "changed Owner"
      case CommentContentType.STRUCTURED_DESCRIPTION:
        return "updated Description"
      case CommentContentType.ACCEPTANCE_CRITERIA:
        return "updated Acceptance Criteria"
      case CommentContentType.SPEC:
        return "updated Spec"
      case CommentContentType.ITEM_TYPE:
        return `${this.before?.itemType === "RETROSPECTIVE_NOTE" ? "сreated Backlog Item from Retrospective Note" : "changed Type"}`
      case CommentContentType.TEAM:
        return "changed Team"
      case CommentContentType.EPIC:
        return "changed Epic"
      case CommentContentType.STORY_POINTS:
        return "changed Story Points"
      case CommentContentType.STATUS:
        return "updated Status"
      case CommentContentType.REGULAR: {
        if (this.repliedOnComment?.id) {
          return `replied to ${this.repliedOnComment.owner.fullName}`
        }
        return "left a comment"
      }
      case CommentContentType.SPRINT: {
        if (this.before?.sprintSequenceNumber && this.before?.sprintStatus === "STARTED") {
          return "removed Item from Active Sprint"
        }
        return "moved Item"
      }
      case CommentContentType.BACKLOG_PRIORITY:
        return "moved Item"
      case CommentContentType.SEVERITY_LEVEL:
        return "updated Severity"
      case CommentContentType.TASKS:
        return this.itemAuditRecord?.taskBefore ? "updated Task" : "added Task"
      case CommentContentType.DEFINITIONS_OF_DONE:
        return this.getDefinitionOfDoneStatusText()
      case CommentContentType.MOVE_TO_TASK:
        return "moved Task to New Item"
      case CommentContentType.DELETE_TASK:
        return "deleted Task"
      case CommentContentType.RESTORE_TASK:
        return "restored Task"
      case CommentContentType.FILE_METADATA_IS_DELETED:
        return "deleted File"
      case CommentContentType.CONTENT_CHANGE:
        return "edited Comment"
      case CommentContentType.DELETED:
        return "deleted Comment"
      case CommentContentType.ITEM_CONVERTED_TO_EPIC:
        return "converted Item to Epic"
      case CommentContentType.IDEA_READY:
        return this.after?.ready ? "marked Idea as Ready" : "marked Idea as Not Ready"
      case CommentContentType.IDEA_STATUS:
        return "updated Status"
      case CommentContentType.IDEA_TITLE:
        return "updated Title"
      case CommentContentType.IDEA_SIZE:
        return "changed Idea Size"
      case CommentContentType.IDEA_CONVERTED_TO_EPIC:
        return "converted Idea to Epic"
      case CommentContentType.IDEA_CONVERTED_TO_FEATURE:
        return "Promoted Idea to Roadmap Feature"
      case CommentContentType.IDEA_CONVERTED_TO_ITEM:
        return "converted Idea to Backlog Item"
      case CommentContentType.IDEA_CONVERTED_RESOURCE:
        return this.getIdeaConvertedResourceText()
      case CommentContentType.DEMO_VIDEO: {
        const hadDemo = !!(
          this.before?.demoFile?.demoVideoUrl || this.before?.demoFile?.demoFileMetadata
        )
        const hasDemo = !!(
          this.after?.demoFile?.demoVideoUrl || this.after?.demoFile?.demoFileMetadata
        )
        if (!hadDemo && hasDemo) return "uploaded Demo Video"
        if (hadDemo && !hasDemo) return "deleted Demo Video"
        return "uploaded Demo Video"
      }
      default:
        return "audit comment"
    }
  }

  get contentComponentName(): string {
    if (this.isRegularComment) {
      return "REGULAR_COMMENT"
    }
    if (this.unimplemented) {
      return "AUDIT_COMMENT_UNIMPLEMENTED"
    }
    return `AUDIT_COMMENT_${this.type}_CHANGED`
  }

  get before(): Partial<CommentItemAuditItem> | null {
    const epicAuditRecord = this.epicAuditRecord
    const change = epicAuditRecord?.change
    if (change && change in epicCommentTypeByAuditRecord) {
      return {
        title: epicAuditRecord.before?.name ?? null,
        structuredDescription: epicAuditRecord.before?.structuredDescription ?? null
      }
    }
    if (this.ideaAuditRecord) {
      return {
        ready: this.ideaAuditRecord.before?.ready ?? null,
        title: this.ideaAuditRecord.before?.title ?? null,
        structuredDescription: this.ideaAuditRecord.before?.structuredDescription ?? null
      }
    }
    return this.itemAuditRecord?.before ?? null
  }

  get after(): Partial<CommentItemAuditItem> | null {
    const epicAuditRecord = this.epicAuditRecord
    const change = epicAuditRecord?.change
    if (change && change in epicCommentTypeByAuditRecord) {
      return {
        title: epicAuditRecord.after?.name ?? null,
        structuredDescription: epicAuditRecord.after?.structuredDescription ?? null
      }
    }
    if (this.ideaAuditRecord) {
      return {
        ready: this.ideaAuditRecord.after?.ready ?? null,
        title: this.ideaAuditRecord.after?.title ?? null,
        structuredDescription: this.ideaAuditRecord.after?.structuredDescription ?? null
      }
    }
    return this.itemAuditRecord?.after ?? null
  }

  static create(props: CommentProps): Comment {
    return new Comment(props)
  }

  static clone(comment: Comment): Comment {
    return Comment.create(structuredClone(comment))
  }
}
